import { useCallback, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// useUnifiedPlanVariant — controls whether the nav shows the dedicated /plan
// route (new Phase 2 task 2.1 surface) or leaves planning folded into the
// dashboard (the Phase 1 arrangement). Default is 'off' until the unified
// plan surface graduates from preview to default.
//
// Follows the same shape as useDashboardVariant for consistency, including
// cross-tab sync via the `storage` event.
// ---------------------------------------------------------------------------

export type UnifiedPlanVariant = 'off' | 'on';

const STORAGE_KEY = 'cadence_unified_plan';
const DEFAULT: UnifiedPlanVariant = 'off';

function readStored(): UnifiedPlanVariant {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'on' || v === 'off' ? v : DEFAULT;
  } catch {
    return DEFAULT;
  }
}

export function useUnifiedPlanVariant() {
  const [variant, setVariantState] = useState<UnifiedPlanVariant>(readStored);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setVariantState(readStored());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setVariant = useCallback((next: UnifiedPlanVariant) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (err) {
      // Surface a warning so the symptom (toggle not persisting across
      // reload) is diagnosable instead of silently swallowed.
      if (err instanceof DOMException && (err.name === 'QuotaExceededError' || err.code === 22)) {
        console.warn(
          'cadence_unified_plan could not be persisted: localStorage quota exhausted. Toggle will reset on reload.',
          err,
        );
      } else {
        console.warn('cadence_unified_plan could not be persisted:', err);
      }
    }
    setVariantState(next);
  }, []);

  return { variant, setVariant, enabled: variant === 'on' };
}
